import React, { useState, useEffect } from 'react';
import { Appointment, ProviderAvailability, DateSpecificAvailability, firebaseAppointmentService } from '../services/firebase/FirebaseAppointmentService';

// Helper function to convert 24-hour time to 12-hour format
const formatTime12Hour = (time24: string): string => {
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

interface CalendarProps {
  appointments: Appointment[];
  onDateClick?: (date: Date) => void;
  onAppointmentClick?: (appointment: Appointment) => void;
  selectedDate?: Date;
  view?: 'month' | 'week' | 'day';
  providerId?: string; // Optional: for showing availability status
  showProviderName?: boolean; // Show provider name on appointment chips (admin view)
}

const Calendar: React.FC<CalendarProps> = ({
  appointments,
  onDateClick,
  onAppointmentClick,
  selectedDate,
  view = 'month',
  providerId,
  showProviderName = false
}) => {
  const [currentDate, setCurrentDate] = useState(selectedDate || new Date());
  const [cachedAvailability, setCachedAvailability] = useState<ProviderAvailability[]>([]);
  const [cachedOverrides, setCachedOverrides] = useState<Map<string, DateSpecificAvailability>>(new Map());

  // Load availability data from Firestore
  useEffect(() => {
    if (!providerId) return;
    const loadAvailability = async () => {
      try {
        const avail = await firebaseAppointmentService.getProviderAvailability(providerId);
        setCachedAvailability(avail);
      } catch (err) {
        console.error('Calendar: failed to load availability', err);
      }
    };
    loadAvailability();
  }, [providerId]);

  // Load date-specific overrides for the visible date range
  useEffect(() => {
    if (!providerId) return;
    const loadOverrides = async () => {
      // Calculate visible date range
      let days: Date[] = [];
      if (view === 'month') {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const firstDayOfWeek = firstDay.getDay();
        for (let i = firstDayOfWeek - 1; i >= 0; i--) days.push(new Date(year, month, -i));
        for (let i = 1; i <= lastDay.getDate(); i++) days.push(new Date(year, month, i));
        const remaining = 42 - days.length;
        for (let i = 1; i <= remaining; i++) days.push(new Date(year, month + 1, i));
      } else {
        const dayOfWeek = currentDate.getDay();
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(currentDate.getDate() - dayOfWeek);
        for (let i = 0; i < 7; i++) {
          const day = new Date(startOfWeek);
          day.setDate(startOfWeek.getDate() + i);
          days.push(day);
        }
      }

      const overrideMap = new Map<string, DateSpecificAvailability>();
      await Promise.all(days.map(async (date) => {
        try {
          const override = await firebaseAppointmentService.getDateSpecificAvailability(providerId, date);
          if (override) {
            const key = date.toISOString().split('T')[0];
            overrideMap.set(key, override);
          }
        } catch {
          // ignore individual override load failures
        }
      }));
      setCachedOverrides(overrideMap);
    };
    loadOverrides();
  }, [providerId, currentDate, view]);

  // Get availability status for a date (synchronous lookup from cached data)
  const getAvailabilityStatus = (date: Date): 'blocked' | 'custom' | 'available' | 'unavailable' | null => {
    if (!providerId) return null;

    const dateKey = date.toISOString().split('T')[0];
    const dateOverride = cachedOverrides.get(dateKey);

    if (dateOverride) {
      return dateOverride.isAvailable ? 'custom' : 'blocked';
    }

    // Check weekly recurring availability
    const dayOfWeek = date.getDay();
    const dayAvailability = cachedAvailability.find(a => a.dayOfWeek === dayOfWeek && a.isActive);

    return dayAvailability ? 'available' : 'unavailable';
  };

  const getDaysInMonth = (date: Date): Date[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days: Date[] = [];

    // Add days from previous month to fill the first week
    const firstDayOfWeek = firstDay.getDay();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const prevDate = new Date(year, month, -i);
      days.push(prevDate);
    }

    // Add days of current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }

    // Add days from next month to complete the grid
    const remainingDays = 42 - days.length; // 6 weeks * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push(new Date(year, month + 1, i));
    }

    return days;
  };

  const getWeekDays = (date: Date): Date[] => {
    const dayOfWeek = date.getDay();
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - dayOfWeek);

    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getAppointmentsForDate = (date: Date): Appointment[] => {
    const dateStr = date.toISOString().split('T')[0];
    return appointments.filter(appt => {
      const apptDateStr = new Date(appt.appointmentDate).toISOString().split('T')[0];
      return apptDateStr === dateStr; // Show all appointments including cancelled
    });
  };

  const formatMonth = (date: Date): string => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const formatWeekRange = (startDate: Date): string => {
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);
    return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isCurrentMonth = (date: Date): boolean => {
    return date.getMonth() === currentDate.getMonth();
  };

  const days = view === 'month' ? getDaysInMonth(currentDate) : getWeekDays(currentDate);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div style={{
      backgroundColor: '#1e293b',
      borderRadius: '8px',
      padding: '20px',
      color: '#f9fafb'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h2 style={{ margin: 0, fontSize: '20px' }}>
          {view === 'month' && formatMonth(currentDate)}
          {view === 'week' && formatWeekRange(days[0])}
          {view === 'day' && currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
        </h2>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={goToPrevious}
            style={{
              padding: '8px 16px',
              backgroundColor: '#374151',
              color: '#f9fafb',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            ←
          </button>

          <button
            onClick={goToToday}
            style={{
              padding: '8px 16px',
              backgroundColor: '#6CA6C1',
              color: '#1e293b',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            Today
          </button>

          <button
            onClick={goToNext}
            style={{
              padding: '8px 16px',
              backgroundColor: '#374151',
              color: '#f9fafb',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            →
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div>
        {/* Weekday Headers */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '8px',
          marginBottom: '8px'
        }}>
          {weekDays.map(day => (
            <div
              key={day}
              style={{
                textAlign: 'center',
                fontSize: '12px',
                fontWeight: 'bold',
                color: '#d1d5db',
                padding: '8px'
              }}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Days Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '8px'
        }}>
          {days.map((date, index) => {
            const dayAppointments = getAppointmentsForDate(date);
            const isCurrentMonthDay = isCurrentMonth(date);
            const isTodayDate = isToday(date);
            const availabilityStatus = getAvailabilityStatus(date);

            // Check if this date is in the past
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const dateOnly = new Date(date);
            dateOnly.setHours(0, 0, 0, 0);
            const isPastDay = dateOnly < today;

            // Determine background color based on availability
            let bgColor = isTodayDate ? '#374151' : '#28527A';
            if (isPastDay) {
              bgColor = '#1f2937'; // Dark grey for past days
            } else if (availabilityStatus === 'blocked') {
              bgColor = '#7f1d1d'; // Dark red for blocked
            } else if (availabilityStatus === 'custom') {
              bgColor = '#78350f'; // Dark amber for custom hours
            } else if (availabilityStatus === 'available') {
              bgColor = '#065f46'; // Dark green for available
            }

            return (
              <div
                key={index}
                onClick={() => !isPastDay && onDateClick?.(date)}
                style={{
                  minHeight: '100px',
                  padding: '8px',
                  backgroundColor: bgColor,
                  borderRadius: '6px',
                  cursor: isPastDay ? 'default' : onDateClick ? 'pointer' : 'default',
                  opacity: !isCurrentMonthDay ? 0.4 : isPastDay ? 0.5 : 1,
                  border: isTodayDate ? '2px solid #6CA6C1' : 'none',
                  transition: 'all 0.2s',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  if (onDateClick) {
                    const lighter = availabilityStatus === 'blocked' ? '#991b1b' :
                                   availabilityStatus === 'custom' ? '#92400e' :
                                   availabilityStatus === 'available' ? '#047857' : '#3b5875';
                    e.currentTarget.style.backgroundColor = lighter;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = bgColor;
                }}
              >
                {/* Date Number with Status Indicator */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '4px'
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: isTodayDate ? 'bold' : 'normal',
                    color: isTodayDate ? '#6CA6C1' : '#f9fafb'
                  }}>
                    {date.getDate()}
                  </div>

                  {/* Availability Status Badge */}
                  {availabilityStatus && (
                    <div style={{
                      fontSize: '16px',
                      lineHeight: 1
                    }} title={
                      availabilityStatus === 'blocked' ? 'Blocked - Unavailable' :
                      availabilityStatus === 'custom' ? 'Custom Hours' :
                      availabilityStatus === 'available' ? 'Available' : 'No Availability Set'
                    }>
                      {availabilityStatus === 'blocked' ? '🚫' :
                       availabilityStatus === 'custom' ? '⚠️' :
                       availabilityStatus === 'available' ? '✅' : ''}
                    </div>
                  )}
                </div>

                {/* Appointments */}
                <div style={{ fontSize: '11px' }}>
                  {dayAppointments.slice(0, 3).map((appt, idx) => (
                    <div
                      key={appt.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAppointmentClick?.(appt);
                      }}
                      style={{
                        padding: '2px 4px',
                        marginBottom: '2px',
                        backgroundColor: appt.status === 'cancelled'
                          ? '#6b7280'
                          : (appt.modality === 'video' ? '#3b82f6' : '#10b981'),
                        borderRadius: '3px',
                        cursor: onAppointmentClick ? 'pointer' : 'default',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        opacity: appt.status === 'cancelled' ? 0.6 : 1,
                        textDecoration: appt.status === 'cancelled' ? 'line-through' : 'none'
                      }}
                      title={`${formatTime12Hour(appt.startTime)} - ${appt.patientName || 'Patient'}${showProviderName && appt.providerName ? ` (${appt.providerName})` : ''} (${appt.modality})${appt.status === 'cancelled' ? ' - CANCELLED' : ''}`}
                    >
                      {formatTime12Hour(appt.startTime)} {appt.status === 'cancelled' ? '🚫' : (appt.modality === 'video' ? '📹' : '👤')}
                      {showProviderName && appt.providerName && (
                        <span style={{ fontSize: '9px', opacity: 0.85 }}> {appt.providerName.split(' ')[0]}</span>
                      )}
                    </div>
                  ))}
                  {dayAppointments.length > 3 && (
                    <div style={{
                      fontSize: '10px',
                      color: '#d1d5db',
                      marginTop: '2px'
                    }}>
                      +{dayAppointments.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginTop: '16px',
        fontSize: '12px',
        color: '#d1d5db',
        flexWrap: 'wrap'
      }}>
        {/* Appointment Types */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <div style={{ width: '12px', height: '12px', backgroundColor: '#3b82f6', borderRadius: '2px' }} />
          <span>Video</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <div style={{ width: '12px', height: '12px', backgroundColor: '#10b981', borderRadius: '2px' }} />
          <span>In-Person</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <div style={{ width: '12px', height: '12px', backgroundColor: '#6b7280', borderRadius: '2px', opacity: 0.6 }} />
          <span>Cancelled</span>
        </div>

        {/* Availability Status (only show if providerId is set) */}
        {providerId && (
          <>
            <div style={{ width: '1px', height: '16px', backgroundColor: '#4b5563', margin: '0 4px' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>✅</span>
              <span>Available</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>⚠️</span>
              <span>Custom Hours</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>🚫</span>
              <span>Blocked</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Calendar;
